import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PrFormService } from '@app/components/pr-form/pr-form.service';
import { OwnerShare } from '@app/components/pr-form/pr-input-owner-share/pr-input-owner-share.component';
import { UtilsService } from '@app/providers/utils/utils.service';
import {
  createLoanPaliersArray,
  withLoanFormValidity,
} from '@app/services/loan/loan-palier.form';
import {
  applyPaliersToLoanPayload,
  hasLoanPaliers,
} from '@app/services/loan/loan-palier.utils';
import * as _ from 'lodash';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EaEstateLoanFormService {
  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected utilsService: UtilsService,
    public prFormService: PrFormService,
  ) {}
  generateSubject: Subject<UntypedFormGroup> = new Subject();

  public generate(entity: any, optionalValues?: any) {
    let options: AbstractControlOptions = {};
    let group = this.formBuilder.group(
      {
        id: [entity ? entity.id : null],
        accountId: [entity ? entity.accountId : null],
        delegationAssurance: [
          entity && !_.isNil(entity.delegationAssurance)
            ? entity.delegationAssurance
            : false,
        ],
        label: [entity ? entity.label : null],
        hasLoan: [
          entity && entity.id ? true : false,
          this.prFormService.RefreshEveryValidators(),
        ],
        knowTotal: [true],
        amountBorrowed: [
          entity && entity.id ? entity.amountBorrowed : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('hasLoan')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        durationTotalInMonth: [
          entity && entity.id ? entity.durationTotalInMonth : 180,
        ],
        paliersEnabled: [hasLoanPaliers(entity)],
        paliers: createLoanPaliersArray(this.formBuilder, entity?.paliers),
        startDate: [entity && entity.id ? entity.startDate : null],
        signatureDate: [entity && entity.id ? entity.signatureDate : null],
        category: [entity && entity.category ? entity.category : null],
        interestRate: [entity && entity.id ? entity.interestRate * 100 : 1],
        insuranceRate: [entity && entity.id ? entity.insuranceRate * 100 : 0.3],
        subscriptionId: [entity && entity.id ? entity.subscriptionId : null],
        // Détenteurs + part de détention (`share`, fraction 0..1).
        owners: [this.buildInitialOwners(entity, optionalValues)],
      },
      options,
    );
    withLoanFormValidity(group);
    this.generateSubject.next(group);

    return group;
  }

  /**
   * Valeur initiale du champ `owners` (détenteurs + part de détention).
   * - prêt existant : on reprend `entity.owners` ;
   * - prêt legacy (anciens `ownerIds`) : répartition égale 1/n ;
   * - nouveau prêt : titulaire principal à 100 % (si fourni via optionalValues).
   */
  buildInitialOwners(entity: any, optionalValues?: any): OwnerShare[] {
    if (entity?.owners?.length) {
      return entity.owners.map((o: any) => ({
        ownerId: o.ownerId,
        share: o.share != null ? o.share : null,
      }));
    }
    if (entity?.ownerIds?.length) {
      const share = Math.round((1 / entity.ownerIds.length) * 100) / 100;
      return entity.ownerIds.map((id: number) => ({ ownerId: id, share }));
    }
    const mainId = optionalValues?.mainOwnerId ?? null;
    return mainId != null ? [{ ownerId: mainId, share: 1 }] : [];
  }

  public extract(form: UntypedFormGroup, nestedForms?) {
    if (!form.value.hasLoan) return undefined;
    let dto = [
      {
        type: 'FIX',
        id: this.utilsService.emptyIsNull(form.value.id),
        accountId: this.utilsService.emptyIsNull(form.value.accountId),
        label: this.utilsService.emptyIsNull(form.value.label),
        amountBorrowed: this.utilsService.emptyIsNull(
          form.value.amountBorrowed,
        ),
        durationTotalInMonth: this.utilsService.emptyIsNull(
          form.value.durationTotalInMonth,
        ),
        startDate: this.utilsService.toIsoDate(form.value.startDate),
        signatureDate: this.utilsService.toIsoDate(form.value.signatureDate),
        category: this.utilsService.emptyIsNull(form.value.category),
        interestRate:
          this.utilsService.emptyIsNull(form.value.interestRate) / 100,
        insuranceRate:
          this.utilsService.emptyIsNull(form.value.insuranceRate) / 100,
        subscriptionId: this.utilsService.emptyIsNull(
          form.value.subscriptionId,
        ),
        delegationAssurance: this.utilsService.emptyIsNull(
          form.value.delegationAssurance,
        ),
        // Détenteurs + part de détention. `ownerIds` est déprécié.
        owners: form.value.owners,
      },
    ];
    applyPaliersToLoanPayload(dto[0], form.value);

    return dto;
  }
}
